import { addDoc, collection, getDocs, Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";
import { categories } from "../utils/static-data";
import CustomSelect from "./common/CustomSelect";
// import { sendSms } from "../utils/sendSms";

const ComplaintForm = ({ onClose }) => {
    const { user } = useAuth();
    const [complaintCount, setComplaintCount] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState("customer"); // customer, office, technical

    const initialForm = {
        customerName: "",
        customerPhone: "",
        address: "",
        description: "",
        category: "",
        department: "",
        approximateResolveTime: "",
        onu: { power: "", pon: "", lan: "" },
        router: { power: "", wanLan: "", wifi: "" },
    };

    const [form, setForm] = useState(initialForm);

    // Options
    const onuPowerOptions = ["on", "off"];
    const onuPONOptions = ["red", "green", "blinking", "no light"];
    const onuLANOptions = ["on", "off"];
    const routerPowerOptions = ["on", "off"];
    const routerWANLANOptions = ["off", "red", "green", "green blinking"];
    const routerWifiOptions = ["Name changed", "same as previous", "connected no internet", "not connecting"];
    const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

    // Count existing complaints
    useEffect(() => {
        const fetchCount = async () => {
            try {
                const snapshot = await getDocs(collection(db, "complaints"));
                setComplaintCount(snapshot.size);
            } catch (err) {
                console.error(err);
            }
        };
        fetchCount();
    }, []);

    // Handle normal input
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // Handle nested select changes (onu/router)
    const handleSelectChange = (name, value, group = null) => {
        if (group) {
            setForm((prev) => ({
                ...prev,
                [group]: { ...prev[group], [name]: value },
            }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.customerName || !form.customerPhone) {
            return toast.error("Please fill all required fields");
        }

        // Validate Red signal specific fields
        if (form.category === "Red signal") {
            if (!form.onu.power || !form.onu.pon || !form.onu.lan) {
                return toast.error("Please fill all ONU status fields for Red signal");
            }
            if (!form.router.power || !form.router.wanLan || !form.router.wifi) {
                return toast.error("Please fill all Router status fields for Red signal");
            }
        }

        try {
            setSubmitting(true);
            const ticketNumber = `TICKET-${complaintCount + 1}`;

            // Prepare data - only include onu/router if category is Red signal
            const complaintData = {
                customerInfo: {
                    name: form.customerName,
                    phone: form.customerPhone,
                    address: form.address,
                },
                category: form.category,
                description: form.description,
                approximateResolveTime: form.approximateResolveTime,
                status: "pending",
                ticketNumber,
                createdBy: user.uid,
                createdAt: Timestamp.now(),
                activityLogs: [
                    {
                        time: Timestamp.now(),
                        message: "Complaint created by Helpdesk",
                        userId: user.uid,
                    },
                ],
            };

            // Only add onu and router if category is Red signal
            if (form.category === "Red signal") {
                complaintData.onu = form.onu;
                complaintData.router = form.router;
            }

            const docRef = await addDoc(collection(db, "complaints"), complaintData);

            // Create complaint tracking URL
            const complaintUrl = `${window.location.origin}/complaint/${docRef.id}`;

            toast.success(`Complaint submitted successfully (Ticket: ${ticketNumber})`);
            onClose?.();
            setForm(initialForm);
            setComplaintCount((prev) => prev + 1);

            // Bangla message with link
            const banglaMessage = `
প্রিয় ${form.customerName},

আপনার অভিযোগটি আমাদের সিস্টেমে গৃহীত হয়েছে।
আপনি নিচের লিঙ্ক থেকে অভিযোগের অবস্থা জানতে পারবেন:

${complaintUrl}

ধন্যবাদান্তে,
বিজয় আইটি সাপোর্ট টিম
`;

            // Send SMS
            if (form.customerPhone) {
                const result = await fetch("https://us-central1-ispadmin-7fb7e.cloudfunctions.net/sendSms", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        number: form.customerPhone.startsWith("88") ? form.customerPhone : `88${form.customerPhone}`,
                        message: banglaMessage,
                    }),
                });

                const data = await result.json();
                console.log("📩 SMS sent:", data);
            }

        } catch (err) {
            console.error(err);
            toast.error("Error submitting complaint");
        } finally {
            setSubmitting(false);
        }
    };

    // Check if Red signal is selected
    const isRedSignal = form.category === "Red signal";

    return (
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-white">New Complaint</h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 transition"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Progress Tabs */}
            <div className="border-b border-gray-200 px-6">
                <div className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab("customer")}
                        className={`py-3 px-1 border-b-2 font-medium text-sm transition ${activeTab === "customer"
                            ? "border-pink-500 text-pink-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Customer Info
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab("office")}
                        className={`py-3 px-1 border-b-2 font-medium text-sm transition ${activeTab === "office"
                            ? "border-pink-500 text-pink-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Office Info
                        </span>
                    </button>
                    {isRedSignal && (
                        <button
                            onClick={() => setActiveTab("technical")}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition ${activeTab === "technical"
                                ? "border-pink-500 text-pink-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                        >
                            <span className="flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Technical Status
                            </span>
                        </button>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
                {/* Category Selection - Always visible at top */}
                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Complaint Category <span className="text-red-500">*</span>
                    </label>
                    <CustomSelect
                        name="category"
                        placeholder="Select Category"
                        options={categories}
                        value={form.category}
                        onChange={(name, value) => {
                            handleSelectChange(name, value);
                            // Reset technical fields if category changes from Red signal
                            if (value !== "Red signal") {
                                setForm(prev => ({
                                    ...prev,
                                    onu: { power: "", pon: "", lan: "" },
                                    router: { power: "", wanLan: "", wifi: "" }
                                }));
                            }
                        }}
                        className="max-w-md"
                    />
                    {form.category && (
                        <p className="mt-2 text-xs text-gray-500">
                            Selected: <span className="font-medium text-pink-600">{form.category}</span>
                        </p>
                    )}
                </div>

                {/* Customer Information Tab */}
                {activeTab === "customer" && (
                    <div className="space-y-6 animate-fadeIn">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Customer Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        name="customerName"
                                        value={form.customerName}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                                        placeholder="Enter customer name"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Contact Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        name="customerPhone"
                                        value={form.customerPhone}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                                        placeholder="Enter phone number"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Address
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <textarea
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                                    placeholder="Enter customer address"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Office Information Tab */}
                {activeTab === "office" && (
                    <div className="space-y-6 animate-fadeIn">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Approximate Resolve Time
                                </label>
                                <CustomSelect
                                    name="approximateResolveTime"
                                    placeholder="Select expected resolution time"
                                    options={resolveTimeOptions}
                                    value={form.approximateResolveTime}
                                    onChange={handleSelectChange}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                                placeholder="Describe the complaint in detail..."
                            />
                        </div>

                        {/* Navigation hint */}
                        {isRedSignal && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm text-blue-700 font-medium">Technical Status Required</p>
                                        <p className="text-sm text-blue-600 mt-1">
                                            Since you selected "Red signal", please proceed to the Technical Status tab to provide ONU and Router information.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Technical Status Tab - Only shown for Red signal */}
                {activeTab === "technical" && isRedSignal && (
                    <div className="space-y-8 animate-fadeIn">
                        {/* ONU Status Section */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800">ONU Status</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Power <span className="text-red-500">*</span>
                                    </label>
                                    <CustomSelect
                                        name="power"
                                        placeholder="Select power status"
                                        options={onuPowerOptions}
                                        value={form.onu.power}
                                        onChange={(name, value) => handleSelectChange(name, value, "onu")}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        PON <span className="text-red-500">*</span>
                                    </label>
                                    <CustomSelect
                                        name="pon"
                                        placeholder="Select PON status"
                                        options={onuPONOptions}
                                        value={form.onu.pon}
                                        onChange={(name, value) => handleSelectChange(name, value, "onu")}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        LAN <span className="text-red-500">*</span>
                                    </label>
                                    <CustomSelect
                                        name="lan"
                                        placeholder="Select LAN status"
                                        options={onuLANOptions}
                                        value={form.onu.lan}
                                        onChange={(name, value) => handleSelectChange(name, value, "onu")}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Router Status Section */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800">Router Status</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Power <span className="text-red-500">*</span>
                                    </label>
                                    <CustomSelect
                                        name="power"
                                        placeholder="Select power status"
                                        options={routerPowerOptions}
                                        value={form.router.power}
                                        onChange={(name, value) => handleSelectChange(name, value, "router")}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        WAN/LAN <span className="text-red-500">*</span>
                                    </label>
                                    <CustomSelect
                                        name="wanLan"
                                        placeholder="Select WAN/LAN status"
                                        options={routerWANLANOptions}
                                        value={form.router.wanLan}
                                        onChange={(name, value) => handleSelectChange(name, value, "router")}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Wifi <span className="text-red-500">*</span>
                                    </label>
                                    <CustomSelect
                                        name="wifi"
                                        placeholder="Select wifi status"
                                        options={routerWifiOptions}
                                        value={form.router.wifi}
                                        onChange={(name, value) => handleSelectChange(name, value, "router")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between items-center">
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Submitting...
                                </span>
                            ) : (
                                "Submit Complaint"
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>

                    {/* Tab Navigation Buttons */}
                    <div className="flex gap-2">
                        {activeTab !== "customer" && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (activeTab === "office") setActiveTab("customer");
                                    if (activeTab === "technical") setActiveTab("office");
                                }}
                                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                            >
                                Previous
                            </button>
                        )}
                        {activeTab === "customer" && (
                            <button
                                type="button"
                                onClick={() => setActiveTab("office")}
                                className="px-4 py-2 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
                            >
                                Next: Office Info
                            </button>
                        )}
                        {activeTab === "office" && isRedSignal && (
                            <button
                                type="button"
                                onClick={() => setActiveTab("technical")}
                                className="px-4 py-2 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
                            >
                                Next: Technical Status
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ComplaintForm;

// import { addDoc, collection, getDocs, Timestamp } from "firebase/firestore";
// import { useEffect, useState } from "react";
// import toast from "react-hot-toast";
// import { useAuth } from "../hooks/useAuth";
// import { db } from "../network/firebaseConfig";
// import { categories } from "../utils/static-data";
// import CustomSelect from "./common/CustomSelect";
// // import { sendSms } from "../utils/sendSms";

// const ComplaintForm = ({ onClose }) => {
//     const { user } = useAuth();
//     const [complaintCount, setComplaintCount] = useState(0);
//     const [submitting, setSubmitting] = useState(false);

//     const initialForm = {
//         customerName: "",
//         customerPhone: "",
//         address: "",
//         description: "",
//         category: "",
//         department: "",
//         approximateResolveTime: "",
//         onu: { power: "", pon: "", lan: "" },
//         router: { power: "", wanLan: "", wifi: "" },
//     };

//     const [form, setForm] = useState(initialForm);

//     // const departments = ["Network", "Fiber", "Support"];
//     const onuPowerOptions = ["on", "off"];
//     const onuPONOptions = ["red", "green", "blinking", "no light"];
//     const onuLANOptions = ["on", "off"];
//     const routerPowerOptions = ["on", "off"];
//     const routerWANLANOptions = ["off", "red", "green", "green blinking"];
//     const routerWifiOptions = ["Name changed", "same as previous", "connected no internet", "not connecting"];


//     // Count existing complaints
//     useEffect(() => {
//         const fetchCount = async () => {
//             try {
//                 const snapshot = await getDocs(collection(db, "complaints"));
//                 setComplaintCount(snapshot.size);
//             } catch (err) {
//                 console.error(err);
//             }
//         };
//         fetchCount();
//     }, []);

//     // Handle normal input
//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setForm((prev) => ({ ...prev, [name]: value }));
//     };

//     // Handle nested select changes (onu/router)
//     const handleSelectChange = (name, value, group = null) => {
//         if (group) {
//             setForm((prev) => ({
//                 ...prev,
//                 [group]: { ...prev[group], [name]: value },
//             }));
//         } else {
//             setForm((prev) => ({ ...prev, [name]: value }));
//         }
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         if (!form.customerName || !form.customerPhone) {
//             return toast.error("Please fill all required fields");
//         }

//         try {
//             setSubmitting(true);
//             const ticketNumber = `TICKET-${complaintCount + 1}`;

//             // 1️⃣ Add complaint to Firestore and capture the new document reference
//             const docRef = await addDoc(collection(db, "complaints"), {
//                 customerInfo: {
//                     name: form.customerName,
//                     phone: form.customerPhone,
//                     address: form.address,
//                 },
//                 category: form.category,
//                 description: form.description,
//                 approximateResolveTime: form.approximateResolveTime,
//                 onu: form.onu,
//                 router: form.router,
//                 status: "pending",
//                 ticketNumber,
//                 createdBy: user.uid,
//                 createdAt: Timestamp.now(),
//                 activityLogs: [
//                     {
//                         time: Timestamp.now(),
//                         message: "Complaint created by Helpdesk",
//                         userId: user.uid,
//                     },
//                 ],
//             });

//             // 2️⃣ Create complaint tracking URL
//             const complaintUrl = `${window.location.origin}/complaint/${docRef.id}`;

//             toast.success(`Complaint submitted successfully (Ticket: ${ticketNumber})`);
//             onClose?.();
//             setForm(initialForm);
//             setComplaintCount((prev) => prev + 1);

//             // 3️⃣ Bangla message with link
//             const banglaMessage = `
// প্রিয় ${form.customerName},

// আপনার অভিযোগটি আমাদের সিস্টেমে গৃহীত হয়েছে।
// আপনি নিচের লিঙ্ক থেকে অভিযোগের অবস্থা জানতে পারবেন:

// ${complaintUrl}

// ধন্যবাদান্তে,
// বিজয় আইটি সাপোর্ট টিম
// `;

//             // 4️⃣ Send SMS
//             if (form.customerPhone) {
//                 // const result = await sendSms({
//                 //     number: form.customerPhone.startsWith("88")
//                 //         ? form.customerPhone
//                 //         : `88${form.customerPhone}`,
//                 //     message: banglaMessage,
//                 // });

//                 // const result = await fetch(`${server}/sms/send-sms`, {
//                 //     method: "POST",
//                 //     headers: { "Content-Type": "application/json" },
//                 //     body: JSON.stringify({
//                 //         number: form.customerPhone.startsWith("88")
//                 //             ? form.customerPhone
//                 //             : `88${form.customerPhone}`,
//                 //         message: banglaMessage,
//                 //     }),
//                 // });


//                 const result = await fetch("https://us-central1-ispadmin-7fb7e.cloudfunctions.net/sendSms", {
//                     method: "POST",
//                     headers: { "Content-Type": "application/json" },
//                     body: JSON.stringify({
//                         number: form.customerPhone.startsWith("88") ? form.customerPhone : `88${form.customerPhone}`,
//                         message: banglaMessage,
//                     }),
//                 });

//                 const data = await result.json(); // read the body **once**
//                 console.log("📩 SMS sent:", data);
//             }

//         } catch (err) {
//             console.error(err);
//             toast.error("Error submitting complaint");
//         } finally {
//             setSubmitting(false);
//         }
//     };


//     // Inside your component
//     const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

//     return (
//         <div className="max-w-4xl mx-auto bg-white p-6 rounded shadow space-y-6">
//             <h2 className="text-xl font-semibold mb-4">New Complaint</h2>
//             <form onSubmit={handleSubmit} className="space-y-6">

//                 <div className="flex flex-col col-span-2 items-start">
//                     <label className="block font-medium mb-1">Category</label>
//                     <CustomSelect
//                         name="category"
//                         placeholder="Select Category"
//                         options={categories}
//                         value={form.category}
//                         onChange={handleSelectChange}
//                     />
//                 </div>
//                 {/* ONU Status */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">Customer Information</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                             {/* we need to update it with customer information from backend, we will take customer id from it */}
//                             <label className="block font-medium mb-1">Customer Name</label>
//                             <input
//                                 type="text"
//                                 name="customerName"
//                                 value={form.customerName}
//                                 onChange={handleChange}
//                                 className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                                 required
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">Contact Number</label>
//                             <input
//                                 type="text"
//                                 name="customerPhone"
//                                 value={form.customerPhone}
//                                 onChange={handleChange}
//                                 className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                                 required
//                             />
//                         </div>
//                     </div>
//                     <div>
//                         <label className="block font-medium mb-1">Address</label>
//                         <textarea
//                             name="address"
//                             value={form.address}
//                             onChange={handleChange}
//                             className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                         />
//                     </div>
//                 </div>

//                 {/* Customer information */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">Office Information</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                             <label className="block font-medium mb-1">Approximate Resolve Time</label>
//                             <CustomSelect
//                                 name="approximateResolveTime"
//                                 placeholder="Select Time"
//                                 options={resolveTimeOptions}
//                                 value={form.approximateResolveTime}
//                                 onChange={handleSelectChange}
//                             />
//                         </div>
//                     </div>
//                 </div>

//                 {/* ONU Status */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">ONU Status</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                         <div>
//                             <label className="block font-medium mb-1">Power</label>
//                             <CustomSelect
//                                 name="power"
//                                 placeholder="ONU Power"
//                                 options={onuPowerOptions}
//                                 value={form.onu.power}
//                                 onChange={(name, value) => handleSelectChange(name, value, "onu")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">PON</label>
//                             <CustomSelect
//                                 name="pon"
//                                 placeholder="ONU PON"
//                                 options={onuPONOptions}
//                                 value={form.onu.pon}
//                                 onChange={(name, value) => handleSelectChange(name, value, "onu")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">LAN</label>
//                             <CustomSelect
//                                 name="lan"
//                                 placeholder="ONU LAN"
//                                 options={onuLANOptions}
//                                 value={form.onu.lan}
//                                 onChange={(name, value) => handleSelectChange(name, value, "onu")}
//                             />
//                         </div>
//                     </div>
//                 </div>

//                 {/* Router Status */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">Router Status</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                         <div>
//                             <label className="block font-medium mb-1">Power</label>
//                             <CustomSelect
//                                 name="power"
//                                 placeholder="Router Power"
//                                 options={routerPowerOptions}
//                                 value={form.router.power}
//                                 onChange={(name, value) => handleSelectChange(name, value, "router")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">WAN/LAN</label>
//                             <CustomSelect
//                                 name="wanLan"
//                                 placeholder="Router WAN/LAN"
//                                 options={routerWANLANOptions}
//                                 value={form.router.wanLan}
//                                 onChange={(name, value) => handleSelectChange(name, value, "router")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">Wifi</label>
//                             <CustomSelect
//                                 name="wifi"
//                                 placeholder="Router Wifi"
//                                 options={routerWifiOptions}
//                                 value={form.router.wifi}
//                                 onChange={(name, value) => handleSelectChange(name, value, "router")}
//                             />
//                         </div>
//                     </div>
//                 </div>

//                 <div>
//                     <label className="block font-medium mb-1">Description</label>
//                     <textarea
//                         name="description"
//                         value={form.description}
//                         onChange={handleChange}
//                         className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                     />
//                 </div>

//                 <div className="flex">
//                     <div className="flex gap-3">
//                         <button type="submit"
//                             className="px-3 bg-pink-500 hover:pink-red-600 text-white text-sm py-1 rounded-md"
//                             disabled={submitting}>
//                             Submit Complaint
//                         </button>

//                         <button
//                             className="px-5 bg-gray-300 hover:pink-red-600 text-gray-900 text-sm py-1 rounded-md"
//                             disabled={submitting}
//                             onClick={onClose}
//                         >
//                             Cancel
//                         </button>
//                     </div>
//                     <div className="flex"></div>
//                 </div>
//             </form>
//         </div>
//     );
// };

// export default ComplaintForm;